import { useEffect } from 'react'
import { useSlsStateContext, useSlsHighlightsContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext } from '../providers/r2Provider/R2Context'

/**
 * Custom hook that properly sets highlights on both main and split screen images
 * Follows the pattern from the old PersonOverview.js implementation
 *
 * @param {Object} options
 * @param {Array<string>} options.elementIds - Field IDs to display highlights for
 * @param {Array<Object>} [options.recordHighlights] - Record-level highlights to display (the
 * record's own outline rects, e.g. from getRecordHighlights). Passed straight through to
 * zion-sls's setDisplayHighlights, which otherwise derives these from its own internal edit
 * state — bypassing that and setting the record display API directly elsewhere gets silently
 * overwritten by this same hook's reactive sync effect the next time it runs. Omit it entirely
 * to leave the record display to zion-sls; passing it (even empty) puts this hook in charge of
 * it, including clearing it.
 * @param {boolean} options.enabled - Whether to display highlights (default: true)
 * @returns {void}
 */
export const useDisplayHighlights = ({ elementIds, recordHighlights, enabled = true }) => {
  const { currentImageArk } = useSlsStateContext()
  const { setDisplayHighlights, clearDisplay } = useSlsHighlightsContext()
  const { topMostPanel } = useOverlayContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const r2State = useR2StateContext()

  useEffect(() => {
    // Don't set highlights if disabled or if highlights panel is open
    if (!enabled || topMostPanel() === 'highlightsPanel') {
      return
    }

    // Calculate split screen ARK inside effect (like PersonOverview.js does)
    const splitScreenImageArk = thumbnails?.[r2State?.referenceFocusedIndex]?.ark

    if (currentImageArk && (elementIds?.length > 0 || recordHighlights?.length > 0)) {
      // zion-sls reads an empty elementIds as "no filter" rather than "no fields" (byElementIds
      // short-circuits to true), so a record-only call would otherwise display every field
      // highlight on the image. Reject them all instead, leaving just the record outline.
      const customFilter = elementIds?.length > 0 ? undefined : () => false

      // Call #1: Set main screen highlights
      setDisplayHighlights({
        mainPartitionKey: currentImageArk,
        elementIds,
        recordHighlights,
        customFilter,
      })

      // Call #2: Set split screen highlights (if active)
      // MUST check r2State.splitScreen before making this call
      if (r2State.splitScreen && splitScreenImageArk) {
        setDisplayHighlights({
          splitScreenPartitionKey: splitScreenImageArk,
          elementIds,
          recordHighlights,
          customFilter,
        })
      }
    } else if (currentImageArk) {
      // Clear highlights if no element IDs. clearDisplay's main/splitScreen only cover the field
      // layers, so the record layers need naming too or a previous outline survives - but only
      // clear those when this hook owns them, since callers that pass no recordHighlights leave
      // the record display to zion-sls's own derivation.
      const ownsRecordDisplay = recordHighlights !== undefined
      clearDisplay({
        main: true,
        splitScreen: true,
        mainRecord: ownsRecordDisplay,
        splitScreenRecord: ownsRecordDisplay,
      })
    }
  }, [
    enabled,
    currentImageArk,
    r2State.referenceFocusedIndex,
    r2State.splitScreen,
    elementIds,
    recordHighlights,
    setDisplayHighlights,
    clearDisplay,
    topMostPanel,
    thumbnails,
  ])
}
